/**
 * Get the enum key from its value
 * @param enumObj The enum object
 * @param enumValue The enum value to find the key for
 * @returns The enum key or the original value if not found
 * 
 * @example
 * const StatusEnum = {
 *   DRAFT: 'Rascunho' as const,
 *   OPEN: 'Aberto' as const,
 * };
 * 
 * getEnumKey(StatusEnum, 'Rascunho') // returns 'DRAFT'
 */
export function getEnumKey<T extends Record<string, any>>(
  enumObj: T,
  enumValue: T[keyof T]
): string {
  const entries = Object.entries(enumObj);
  const found = entries.find(([key, value]) => value === enumValue);
  return found ? found[0] : enumValue;
}

/**
 * Get the enum value from its key
 * @param enumObj The enum object
 * @param enumKey The enum key to find the value for
 * @returns The enum value or undefined if not found
 * 
 * @example
 * const StatusEnum = {
 *   DRAFT: 'Rascunho' as const,
 *   OPEN: 'Aberto' as const,
 * };
 * 
 * getEnumValue(StatusEnum, 'DRAFT') // returns 'Rascunho'
 */
export function getEnumValue<T extends Record<string, any>>(
  enumObj: T,
  enumKey: keyof T
): T[keyof T] | undefined {
  return enumObj[enumKey];
}

/**
 * Get all enum keys
 * @param enumObj The enum object
 * @returns Array of enum keys
 */
export function getEnumKeys<T extends Record<string, any>>(
  enumObj: T
): string[] {
  return Object.keys(enumObj);
}

/**
 * Get all enum values
 * @param enumObj The enum object
 * @returns Array of enum values
 */
export function getEnumValues<T extends Record<string, any>>(
  enumObj: T
): T[keyof T][] {
  return Object.values(enumObj);
}
